/**
 * Jobs Table Component
 * 
 * Displays list of scheduled jobs with actions for trigger, enable/disable.
 * Receives runningJobs Set from parent for live status via WebSocket.
 */
import React, { useState } from 'react';
import { FiPlay, FiPower, FiActivity } from 'react-icons/fi';
import { useBreakpoint } from '../../../../hooks/useBreakpoint';

const JobsTable = ({ jobs, runningJobs = new Set(), onTrigger, onToggle, onRefresh }) => {
  //! Phone gets CARDS, not the table. Six columns are ~1130px of natural width
  //! against a 343px screen, and the Actions column is sticky — it alone covered
  //! ~40% of the viewport, leaving under a fifth of the table readable. Sideways
  //! panning reads as broken/cut-off. Desktop keeps the table (it works there).
  const { isPhone } = useBreakpoint();
  const [triggeringJob, setTriggeringJob] = useState(null);
  const [togglingJob, setTogglingJob] = useState(null);
  const [timezoneMode, setTimezoneMode] = useState('local'); // Default to 'local'
  
  // Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    jobId: null,
    jobName: '',
    action: null // 'disable'
  });

  // Get user's local timezone offset in minutes
  const getLocalOffsetMinutes = () => {
    return -new Date().getTimezoneOffset(); // Returns offset from UTC in minutes
  };

  // Get exchange timezone offset based on job's timezone
  const getExchangeOffset = (timezone) => {
    const offsets = {
      'Asia/Kolkata': 330,      // IST: +5:30
      'America/New_York': -300, // EST: -5:00 (or -240 for EDT)
      'Europe/London': 0,       // GMT (or +60 for BST)
      'Asia/Tokyo': 540,        // JST: +9:00
    };
    return offsets[timezone] || 0;
  };

  // Format next run time relative
  const formatNextRun = (dateStr) => {
    if (!dateStr) return 'Not scheduled';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Convert cron expression to human-readable format
  const describeCron = (cron, jobTimezone) => {
    if (!cron) return 'Manual only';
    
    const parts = cron.split(' ');
    if (parts.length !== 5) return cron;
    
    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
    
    // Get offset based on selected mode
    let offsetMinutes = 0;
    let tzLabel = 'UTC';
    
    if (timezoneMode === 'local') {
      offsetMinutes = getLocalOffsetMinutes();
      tzLabel = Intl.DateTimeFormat().resolvedOptions().timeZone.split('/').pop() || 'Local';
    } else if (timezoneMode === 'exchange') {
      offsetMinutes = getExchangeOffset(jobTimezone);
      tzLabel = jobTimezone?.split('/').pop() || 'Exchange';
    }
    
    // Helper: format time with proper AM/PM
    const formatTime = (totalMinutes) => {
      // Handle negative values (wrap around midnight)
      let mins = ((totalMinutes % 1440) + 1440) % 1440;
      const hrs = Math.floor(mins / 60);
      const m = Math.round(mins % 60);
      const period = hrs < 12 ? 'AM' : 'PM';
      const displayHour = hrs === 0 ? 12 : hrs > 12 ? hrs - 12 : hrs;
      if (m === 0) {
        return `${displayHour} ${period}`;
      }
      return `${displayHour}:${m.toString().padStart(2, '0')} ${period}`;
    };
    
    // Parse values
    const cronMinute = parseInt(minute, 10);
    const isIntervalMinute = minute.startsWith('*/');
    
    let freq = '';
    let time = '';
    let days = '';
    
    // Determine frequency type
    if (isIntervalMinute) {
      const interval = minute.substring(2);
      freq = `Every ${interval} min`;
    } else if (hour !== '*') {
      freq = 'Daily';
    }
    
    // Time formatting
    if (hour !== '*') {
      const hourParts = hour.split('-');
      const h1 = parseInt(hourParts[0], 10);
      
      // Convert UTC to selected timezone
      const utcTotalMinutes = h1 * 60 + (isNaN(cronMinute) ? 0 : cronMinute);
      const localMinutes = utcTotalMinutes + offsetMinutes;
      
      if (hourParts.length === 2) {
        // Range like 3-11
        const h2 = parseInt(hourParts[1], 10);
        const endUtcMinutes = h2 * 60 + 59;
        const endLocalMinutes = endUtcMinutes + offsetMinutes;
        time = ` (${formatTime(localMinutes)} - ${formatTime(endLocalMinutes)})`;
      } else {
        // Single hour
        time = ` at ${formatTime(localMinutes)}`;
      }
    }
    
    // Days of week
    const dayMap = { '0': 'Sun', '1': 'Mon', '2': 'Tue', '3': 'Wed', '4': 'Thu', '5': 'Fri', '6': 'Sat' };
    if (dayOfWeek !== '*') {
      if (dayOfWeek === '1-5') {
        days = ', Weekdays';
      } else if (dayOfWeek === '1-6') {
        days = ', Mon-Sat';
      } else if (dayOfWeek === '0-6') {
        days = ', Daily';
      } else {
        const dayNums = dayOfWeek.split(',').map(d => dayMap[d] || d);
        days = `, ${dayNums.join('/')}`;
      }
    }
    
    return { text: `${freq}${time}${days}`.trim(), tzLabel };
  };

  // Handle trigger with loading state
  const handleTrigger = async (jobId) => {
    setTriggeringJob(jobId);
    try {
      await onTrigger(jobId);
    } finally {
      setTriggeringJob(null);
    }
  };

  // Initiate toggle action
  const initiateToggle = (job) => {
    if (job.is_enabled) {
      // If enabling -> disabling, show confirmation
      setConfirmModal({
        isOpen: true,
        jobId: job.id,
        jobName: job.job_name,
        action: 'disable'
      });
    } else {
      // If disabling -> enabling, proceed directly
      handleToggle(job.id, false); // false because currentEnabled is false
    }
  };

  // Verify and execute toggle
  const confirmToggle = async () => {
    if (!confirmModal.jobId) return;
    
    setConfirmModal(prev => ({ ...prev, isOpen: false })); // Close modal
    await handleToggle(confirmModal.jobId, true); // true because currentEnabled was true
  };

  // Handle toggle with loading state
  // currentEnabled: the state BEFORE the toggle click
  const handleToggle = async (jobId, currentEnabled) => {
    setTogglingJob(jobId);
    try {
      await onToggle(jobId, !currentEnabled); // send the NEW desired state (which is !current) -- wait, parent expects (id, NEW_STATE) or (id, CURRENT_STATE)? 
      // Checking parent: onToggle(jobId, !currentEnabled)
      // So if currentEnabled is true (enabled), we send false (disable).
    } finally {
      setTogglingJob(null);
    }
  };

  // Check if job is currently running (via WebSocket updates)
  const isJobRunning = (jobId) => runningJobs.has(jobId);

  // Status badge variant
  const getStatusBadge = (status, jobId) => {
    // If job is currently running, show running status
    if (isJobRunning(jobId)) {
      return { variant: 'info animate-pulse', label: 'running' };
    }

    const variants = {
      success: 'profit',
      failed: 'loss',
      running: 'info',
      timeout: 'warn',
    };
    return { variant: variants[status] || 'neutral', label: status };
  };

  //--> Shared empty state for both presentations.
  const emptyState = (
    <div className="flex flex-col items-center justify-center py-16 text-fg-2">
      <span className="text-4xl mb-2">📭</span>
      <p>No scheduled jobs found</p>
    </div>
  );

  //--> PHONE: one card per job. Everything the table showed is visible without
  //    panning, and Run / Enable-Disable become real labelled buttons instead of
  //    two 30px icons 4px apart — a missed tap there disabled a production job.
  const renderJobCards = () => (
    <div className="flex flex-col gap-3">
      {jobs.map((job) => {
        const schedule = describeCron(job.cron_expression, job.timezone);
        const badge = getStatusBadge(job.last_run_status, job.id);
        const running = isJobRunning(job.id);
        const lastRunTime = job.last_run_at ? new Date(job.last_run_at) : null;

        return (
          <div
            key={job.id}
            //! Milder, always-on treatment for a disabled job: the table dims to
            //! 60% + grayscale and only recovers on hover, which a phone doesn't
            //! have. Legibility can't be gated behind a gesture that can't happen.
            className={`rounded-xl border border-[var(--divider)] bg-bg-2 p-4 ${
              !job.is_enabled ? 'opacity-90' : ''
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-fg-1">{job.job_name}</div>
                {job.description && (
                  <div className="mt-1 text-xs leading-relaxed text-fg-2">{job.description}</div>
                )}
              </div>
              {(badge.label || running) && (
                <span className={`ds-badge sm shrink-0 ${badge.variant}`}>
                  {badge.label === 'running' ? 'Running' : badge.label}
                </span>
              )}
            </div>

            {(job.tags || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {job.tags.map((tag) => (
                  <span key={tag} className="ds-badge xs neutral">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-3 border-t border-[var(--divider)] pt-3 text-xs">
              <div className="col-span-2 min-w-0">
                <dt className="font-semibold text-fg-3">Target</dt>
                <dd className="mt-1 flex flex-wrap items-center gap-2">
                  <code className="rounded border border-[var(--divider)] bg-[var(--badge-info-bg)] px-2 py-1 text-[var(--badge-info-fg)]">
                    {job.target_service}
                  </code>
                  {/* break-all, not truncate: the endpoint is the only thing that
                      says WHAT this job actually calls. */}
                  <span className="min-w-0 break-all font-mono text-fg-2">{job.target_endpoint}</span>
                </dd>
              </div>

              <div className="col-span-2 min-w-0">
                <dt className="font-semibold text-fg-3">Schedule</dt>
                <dd className="mt-1 text-fg-2">
                  <span className="flex items-center gap-1.5">
                    <span className="text-fg-3">⏰</span>
                    {schedule.text || schedule}
                  </span>
                  {job.cron_expression && (
                    <span className="mt-1 flex flex-wrap items-center gap-1 text-[11px]">
                      <span className="rounded bg-bg-3 px-1.5 font-mono">{job.cron_expression}</span>
                      <span>in {schedule.tzLabel || job.timezone}</span>
                    </span>
                  )}
                </dd>
              </div>

              <div className="min-w-0">
                <dt className="font-semibold text-fg-3">Next Run</dt>
                <dd className="mt-1 text-fg-2">
                  {job.is_enabled ? (
                    formatNextRun(job.next_run_at)
                  ) : (
                    <span className="ds-badge xs neutral">Disabled</span>
                  )}
                </dd>
              </div>

              <div className="min-w-0">
                <dt className="font-semibold text-fg-3">Last Run</dt>
                <dd className="mt-1 text-fg-2">
                  {lastRunTime
                    ? lastRunTime.toLocaleString('en-IN', {
                        month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })
                    : '—'}
                </dd>
              </div>
            </dl>

            {/* Real, separated, labelled buttons — the `btn` classes already get
                the 44px coarse-pointer bump from ds-components.css for free. */}
            <div className="mt-4 flex gap-3 border-t border-[var(--divider)] pt-3">
              <button
                type="button"
                onClick={() => handleTrigger(job.id)}
                disabled={triggeringJob === job.id || !job.is_enabled || running}
                className="btn btn-secondary inline-flex flex-1 items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {running || triggeringJob === job.id ? (
                  <FiActivity className="animate-spin" />
                ) : (
                  <FiPlay />
                )}
                {running ? 'Running…' : 'Run Once'}
              </button>

              <button
                type="button"
                onClick={() => !togglingJob && initiateToggle(job)}
                disabled={togglingJob === job.id}
                className={`btn ${
                  job.is_enabled ? 'btn-danger' : 'btn-primary'
                } inline-flex flex-1 items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {togglingJob === job.id ? (
                  <FiActivity className="animate-spin" />
                ) : (
                  <FiPower />
                )}
                {job.is_enabled ? 'Disable' : 'Enable'}
              </button>
            </div>
          </div>
        );
      })}
      {jobs.length === 0 && (
        <div className="rounded-xl border border-[var(--divider)] bg-bg-2">{emptyState}</div>
      )}
    </div>
  );

  const renderJobTable = () => (
    <div className="bg-bg-2/50 border border-[var(--divider)] rounded-xl overflow-hidden shadow-lg backdrop-blur-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          {/* Hidden when empty: a 6-col header scrolling sideways above an
              empty-state panel is a header stranded over nothing. */}
          {jobs.length > 0 && (
            <thead>
              <tr className="text-left text-fg-2 border-b border-[var(--divider)] bg-bg-3/80">
                <th className="py-4 px-6 font-medium">Job Details</th>
                <th className="py-4 px-6 font-medium">Target Service</th>
                <th className="py-4 px-6 font-medium">Schedule</th>
                <th className="py-4 px-6 font-medium">Next Run</th>
                <th className="py-4 px-6 font-medium">Last Status</th>
                <th className="py-4 px-6 font-medium text-right w-32 sticky right-0 bg-bg-3/80 backdrop-blur-sm">Actions</th>
              </tr>
            </thead>
          )}
          <tbody className="divide-y divide-[var(--divider)]">
              {jobs.map((job) => (
                <tr 
                  key={job.id} 
                  className={`
                    transition-all duration-200 hover:bg-bg-3/80
                    ${!job.is_enabled ? 'opacity-60 grayscale-[0.5] hover:opacity-100 hover:grayscale-0' : ''}
                  `}
                >
                  {/* Job Name */}
                  <td className="py-4 px-6 align-top">
                    <div className="font-semibold text-fg-2 text-base">{job.job_name}</div>
                    <div className="text-xs text-fg-2 mt-1 max-w-[200px] leading-relaxed">
                      {job.description}
                    </div>
                    {(job.tags || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {job.tags.map((tag) => (
                          <span
                            key={tag}
                            className="ds-badge xs neutral"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>

                  {/* Target */}
                  <td className="py-4 px-6 align-top">
                    <div className="flex flex-col gap-1">
                      <code className="text-xs bg-[var(--badge-info-bg)] text-[var(--badge-info-fg)] px-2 py-1 rounded w-fit border border-[var(--divider)]">
                        {job.target_service}
                      </code>
                      {/* title was inside the className string — the tooltip never
                          existed and two garbage class names were being injected. */}
                      <span
                        className="text-fg-2 font-mono text-xs truncate max-w-[150px]"
                        title={job.target_endpoint}
                      >
                        {job.target_endpoint}
                      </span>
                    </div>
                  </td>

                  {/* Schedule */}
                  <td className="py-4 px-6 align-top">
                    {(() => {
                      const schedule = describeCron(job.cron_expression, job.timezone);
                      return (
                        <div className="space-y-1">
                          <div className="text-fg-2 text-sm flex items-center gap-1.5">
                            <span className="text-fg-3 text-xs">⏰</span>
                            {schedule.text || schedule}
                          </div>
                          <div className="text-[11px] text-fg-2 flex items-center gap-1">
                            <span className="font-mono bg-bg-3 px-1.5 rounded">{job.cron_expression}</span>
                            <span>in {schedule.tzLabel || job.timezone}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </td>

                  {/* Next Run */}
                  <td className="py-4 px-6 align-top">
                    {job.is_enabled ? (
                      <div className="text-fg-2 text-sm">
                        {formatNextRun(job.next_run_at)}
                      </div>
                    ) : (
                      <span className="ds-badge sm neutral">
                        Disabled
                      </span>
                    )}
                  </td>

                  {/* Last Run */}
                  <td className="py-4 px-6 align-top">
                    {(() => {
                      const badge = getStatusBadge(job.last_run_status, job.id);
                      const lastRunTime = job.last_run_at ? new Date(job.last_run_at) : null;
                      
                      if (badge.label || isJobRunning(job.id)) {
                        return (
                          <div className="space-y-2">
                            <span className={`ds-badge sm ${badge.variant}`}>
                              {badge.label === 'running' ? (
                                <>
                                  <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-current"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-current"></span>
                                  </span>
                                  Running
                                </>
                              ) : (
                                badge.label
                              )}
                            </span>
                            {lastRunTime && (
                              <div className="text-[11px] text-fg-2 pl-1">
                                {lastRunTime.toLocaleString('en-IN', {
                                  month: 'short', day: 'numeric',
                                  hour: '2-digit', minute: '2-digit'
                                })}
                              </div>
                            )}
                          </div>
                        );
                      }
                      return <span className="text-fg-2 text-2xl leading-none">·</span>;
                    })()}
                  </td>

                  {/* Actions */}
                  <td className="py-4 px-6 align-top text-right sticky right-0 bg-bg-2 backdrop-blur shadow-[-8px_0_12px_-4px_rgba(0,0,0,0.1)] dark:shadow-[-8px_0_12px_-4px_rgba(0,0,0,0.3)] border-l border-[var(--divider)]">
                    {/* gap-2 + a 44px hit area, not gap-1 + p-1.5: Run and Disable
                        sat 4px apart at ~30px each, so a missed tap on Run disabled
                        a production job. Raw buttons, so the size is explicit. */}
                    <div className="flex items-center justify-end gap-2">
                       {/* Trigger (Run) Button */}
                      <button
                        onClick={() => handleTrigger(job.id)}
                        disabled={triggeringJob === job.id || !job.is_enabled || isJobRunning(job.id)}
                        className={`inline-flex h-11 w-11 items-center justify-center rounded transition-colors ${
                          isJobRunning(job.id) || triggeringJob === job.id
                            ? 'text-accent bg-accent/10 cursor-wait'
                            : job.is_enabled
                              ? 'text-accent hover:bg-accent/10'
                              : 'text-fg-3 cursor-not-allowed'
                        }`}
                        title={isJobRunning(job.id) ? "Running..." : job.is_enabled ? "Run Once" : "Enable job to run"}
                      >
                         {isJobRunning(job.id) || triggeringJob === job.id ? (
                           <FiActivity className="text-lg animate-spin" />
                         ) : (
                           <FiPlay className="text-lg" />
                         )}
                      </button>

                      {/* Enable/Disable Button */}
                      <button
                        onClick={() => !togglingJob && initiateToggle(job)}
                        disabled={togglingJob === job.id}
                        className={`inline-flex h-11 w-11 items-center justify-center rounded transition-colors ${
                          togglingJob === job.id
                            ? 'text-fg-2 cursor-wait'
                            : job.is_enabled
                              ? 'text-profit hover:bg-profit/10'
                              : 'text-fg-2 hover:text-fg-1 hover:bg-bg-3'
                        }`}
                        title={job.is_enabled ? 'Disable Job' : 'Enable Job'}
                      >
                        {togglingJob === job.id ? (
                          <FiActivity className="text-lg animate-spin" />
                        ) : (
                          <FiPower className="text-lg" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
            ))}
          </tbody>
        </table>
      </div>

      {jobs.length === 0 && emptyState}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header — stacks below sm: the heading plus the timezone segmented and
          refresh button need ~390px against a 343px phone. */}
      <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="ds-h3 text-fg-1">
          Scheduled Jobs
        </h3>
        <div className="flex items-center gap-3">
          {/* Timezone Toggle */}
          <div className="flex bg-bg-3 p-1 rounded-lg border border-[var(--divider)]">
            {['utc', 'local', 'exchange'].map((mode) => (
              <button
                key={mode}
                onClick={() => setTimezoneMode(mode)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                  timezoneMode === mode
                    ? 'bg-accent text-white shadow-sm'
                    : 'text-fg-2 hover:text-fg-1 hover:bg-bg-3/50'
                }`}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
          <button
            onClick={onRefresh}
            className="p-2 text-fg-2 hover:text-fg-1 hover:bg-bg-3 rounded-lg transition-colors"
            title="Refresh List"
          >
            <span className="text-sm">🔄</span>
          </button>
        </div>
      </div>

      {/* Cards on a phone, table on desktop. */}
      {isPhone ? renderJobCards() : renderJobTable()}

      {/* Confirmation Modal */}
      {confirmModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          {/* bg-bg-2, not bg-gray-900: this dialog had no `dark:` prefix, so in
              light theme it was a near-black card with dark-grey body text — the
              user saw a black box and two buttons with no explanation of what
              they were confirming. max-h + overflow-y-auto: in landscape the
              centred card ran off both edges and clipped Cancel / Yes. */}
          <div className="bg-bg-2 border border-[var(--divider)] rounded-2xl shadow-2xl max-w-sm w-full p-6 max-h-[90dvh] overflow-y-auto animate-scaleIn">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-[var(--badge-loss-bg)] rounded-full border border-[var(--divider)]">
                <span className="text-2xl">⚠️</span>
              </div>
              <div>
                <h3 className="ds-h3 text-fg-1">Disable Job?</h3>
                <p className="text-sm text-fg-3 mt-1 leading-relaxed">
                  Are you sure you want to disable
                  <span className="font-medium text-fg-2 block mt-1">
                    "{confirmModal.jobName}"
                  </span>
                  This will stop upcoming scheduled runs.
                </p>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                className="btn btn-secondary btn-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmToggle}
                className="btn btn-danger btn-sm"
              >
                Yes, Disable Job
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobsTable;
